import formatAttendeeName, { concatAttendeeName } from './formatAttendeeName';
import { OTHER_ATTENDEE_REGISTRATION_ID, REQUEST_DATE_STATUS, REQUEST_STATUS,
  ROOMMATE_MATCHING_TYPE } from './travelConstants';
import { defaultMemoize, createSelector } from 'reselect';
import { utcToDisplayDate } from '../redux/modules/timezones';
import { filterRoommates } from '../lib/HotelRequest/utils/LookupRoommateUtil';
import { getRoommateRequestSettings, mapFromRoommateSetupSettings } from '../lib/HotelRequest/utils/HotelRequestUtil';
import { SURVEY_TYPE, QUESTION_DISPLAY_TYPE } from './questionConstants';
import { InviteeStatusId } from './InviteeStatus';

export const DELAY_BEFORE_NEXT_FETCH_MILLIS = 300;
const MIN_SEARCH_LENGTH = 3;
const SHOW_AIRPORTS_WITH_STATE_CODE = [440, 469];


export const RoommateIdType = {
  /**
   * contactId for No response invitee
   */
  CONTACT: 'CONTACT',
  /**
   * hotelReservationDetailId for Accepted invitee
   */
  HOTEL_RESERVATION_DETAIL: 'HOTEL_RESERVATION_DETAIL',
  /**
   * eventRegistrationId for primary invitee
   */
  EVENT_REGISTRATION: 'EVENT_REGISTRATION',
  /**
   * eventRegistrationId for guests
   */
  GUEST_EVENT_REGISTRATION: 'GUEST_EVENT_REGISTRATION'
};

/**
 * Roommate Matching Status for booked roommates
 */
export const RoommateStatus = {
  PENDING: 1,
  ACCEPTED: 2,
  PRE_SELECTED: 3,
  DENIED: 4,
  CANCELLED: 5
};

/*
 * Prints the room preferences for a particular booking preference
 */
// eslint-disable-next-line complexity
export function printRoomPreferences(showPreferences, bookingPreferences, quantity, translate) {
  const isSingleRoomBooked = quantity === 1;

  if (!showPreferences) {
    return isSingleRoomBooked ? translate('EventWidgets_RegSummary_RoomRequested__resx') :
      translate('EventWidgets_RegSummary_RoomsRequested__resx', { number: quantity });
  }

  if (!bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmokingAndADA__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmokingAndADA__resx', { number: quantity });
  } else if (bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithSmokingAndADA__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmokingAndADA__resx', { number: quantity });
  } else if (!bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmoking__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmoking__resx', { number: quantity });
  } else if (bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_RoomWithSmoking__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmoking__resx', { number: quantity });
  }
  return null;
}

export function makeAttendeeRegistrationIdForOther(primaryEventRegistrationId) {
  return `${OTHER_ATTENDEE_REGISTRATION_ID}~${primaryEventRegistrationId}`;
}

export function getSelectedAttendeeId(attendeeRegistrationId) {
  if (attendeeRegistrationId.startsWith(OTHER_ATTENDEE_REGISTRATION_ID)) {
    return attendeeRegistrationId.split('~').pop();
  }
  return attendeeRegistrationId;
}

/**
 * Returns primary attendee display name
 * @param {Object} nameObj
 * @param {string} nameObj.firstName
 * @param {string} nameObj.middleName
 * @param {string} nameObj.lastName
 * @param {Function} translate
 * @returns {string}
 */
function getPrimaryAttendeeDisplayName({ firstName, middleName, lastName }, translate) {
  return (!firstName && !middleName && !lastName)
    ? translate('EventWidgets_AirRequest_PrimaryNoName__resx')
    : formatAttendeeName({ firstName, middleName, lastName });
}


/**
 * make attendee options to display on ui
 * @param {Function} translate
 * @param {Object} registrations
 * @param {Object} registrations.primary primary event registration details
 * @param {Array} registrations.guests guests' registration details
 * @param {Object} settings
 * @param {boolean} settings.showGuests whether should show guest in the dropdown or not
 * @param {Object} settings.attendeeRequestCounts map of existing counts of air requests for the attendees
 * @param {number} settings.attendeeLimit per attendee air request limit
 * @param {boolean} settings.isForExisting whether this render is for an existing request or new request
 * @param {boolean} settings.isPlanner whether planner reg or not
 * @returns {Array}
 */
export function makeAttendeeOptions(
  translate,
  { primary = { attendee: { personalInformation: {} } }, guests = [] } = {},
  { showGuests = true, attendeeRequestCounts = {}, attendeeLimit, isForExisting, isPlanner }) {
  // return empty if don't need to render guests, because default selection value will be of primary
  if (!showGuests) {
    return [];
  }
  const ignoreLimit = isPlanner || isForExisting || !attendeeLimit;
  const attendeeOptionGroups = [];
  const primaryCanRequest = ignoreLimit || attendeeRequestCounts[primary.eventRegistrationId] < attendeeLimit;
  if (primaryCanRequest) {
    attendeeOptionGroups.push({
      category: translate('EventWidgets_AirRequest_Labels_Primary__resx'),
      items: [{
        name: getPrimaryAttendeeDisplayName(primary.attendee.personalInformation, translate),
        value: primary.eventRegistrationId
      }]
    });
  }
  const guestAndOtherOptions = guests
    .filter(guest => ignoreLimit || attendeeRequestCounts[guest.eventRegistrationId] < attendeeLimit)
    .map(guest => ({
      name: formatAttendeeName(guest.attendee.personalInformation),
      value: guest.eventRegistrationId
    }));
  if (primaryCanRequest) {
    guestAndOtherOptions.push({
      name: translate('EventWidgets_AirRequest_Labels_Other__resx'),
      value: makeAttendeeRegistrationIdForOther(primary.eventRegistrationId)
    });
  }
  if (guestAndOtherOptions.length) {
    attendeeOptionGroups.push({
      category: translate('EventWidgets_AirRequest_Labels_Guests__resx'),
      items: guestAndOtherOptions
    });
  }
  return attendeeOptionGroups;
}

/**
 * Checks if search text provided matches min length criteria
 * @param {string} searchText
 * @returns {boolean}
 */
export function hasMinLengthForSearch(searchText) {
  return searchText && searchText.length >= MIN_SEARCH_LENGTH;
}

/**
 * returns the formatted airport name to display on ui
 * @param {Object} airportObj
 * @param {string} airportObj.id
 * @param {string} airportObj.city
 * @param {string} airportObj.stateCode
 * @param {string} airportObj.name
 * @param {string} airportObj.code
 * @param {Function} translate
 * @returns {string}
 */
export function getAirportDisplayName({ id, city, stateCode, name, code }, translate) {
  if (SHOW_AIRPORTS_WITH_STATE_CODE.includes(id)) {
    return translate('EventWidgets_AirRequest_AirportNameWithState__resx', { city, stateCode, name, code });
  }
  return translate('EventWidgets_AirRequest_AirportName__resx', { city, name, code });
}

/**
 * Returns airport suggestions to be display on the suggestive text box
 * @param {string} searchText
 * @param {boolean} buildAll
 * @param {Object} props
 * @returns {Array}
 */
export function makeAirportSuggestions(searchText, buildAll, props) {
  if (props.isSiteEditorPreview) { // site editor can't display suggestions
    return [];
  }
  const { airports, airports: { searchResults = {} } = {}, translate } = props;
  let airportsToSuggest;
  if (buildAll) {
    airportsToSuggest = airports;
  } else {
    if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    airportsToSuggest = searchResults[searchText];
  }
  if (airportsToSuggest) {
    return Object.keys(airportsToSuggest)
      .map(id => {
        return {
          name: getAirportDisplayName(airportsToSuggest[id], translate),
          value: id.toString(),
          sortOrder: airportsToSuggest[id].sortOrder
        };
      }).sort((one, two) => one.sortOrder - two.sortOrder);
  }
}

function getRoommateIdAndType(roommate) {
  let roommateId = null;
  let roommateIdType = null;
  if (roommate.inviteeStatus === InviteeStatusId.NoResponse || roommate.inviteeStatus === InviteeStatusId.Visited) {
    roommateId = roommate.contactId;
    roommateIdType = RoommateIdType.CONTACT;
  } else if (roommate.inviteeStatus === InviteeStatusId.Accepted ||
      roommate.inviteeStatus === InviteeStatusId.PendingApproval) {
    roommateId = roommate.hotelReservationDetailId;
    roommateIdType = RoommateIdType.HOTEL_RESERVATION_DETAIL;
  } else {
    throw new Error(`invalid inviteeStatus [${roommate.inviteeStatus}] for roommate [${JSON.stringify(roommate)}]`);
  }
  return { roommateId, value: roommateId, roommateIdType }; // 'value' is for suggestive box
}

function makeRoommateFromEventReg(registration, isGuest) {
  return {
    roommateId: registration.eventRegistrationId,
    value: registration.eventRegistrationId, // for suggestive box
    roommateIdType: isGuest ? RoommateIdType.GUEST_EVENT_REGISTRATION : RoommateIdType.EVENT_REGISTRATION,
    name: formatAttendeeName({
      firstName: registration.attendee.personalInformation.firstName,
      lastName: registration.attendee.personalInformation.lastName
    }),
    title: registration.attendee.personalInformation.title,
    companyName: registration.attendee.personalInformation.company
  };
}

/**
 * get registrants to display as roommates
 * @type {function(*, *): {guests: [], primaryInvitee: []}}
 */
const getRegistrantsAsRoommates = defaultMemoize((registrations, exclusionIds) => {
  const roommatesList = {
    guests: [],
    primaryInvitee: [] // todo: try make this an object
  };

  if (registrations.primary && registrations.primary.attendee &&
    !exclusionIds.includes(registrations.primary.eventRegistrationId)) {
    const primaryInvitee = [makeRoommateFromEventReg(registrations.primary)];
    roommatesList.primaryInvitee.push(...primaryInvitee);
  }

  const guestRoommates = registrations.guests
    .filter(guest => !exclusionIds.includes(guest.eventRegistrationId))
    .map(guest => makeRoommateFromEventReg(guest, true));
  roommatesList.guests.push(...guestRoommates);

  return roommatesList;
});

/**
 * get filtered registrants to display as roommates
 * @type {Function}
 */
const getFilteredRegistrantsAsRoommates = defaultMemoize((
  registrations, exclusionIds, roommateSearchText
) => {
  const roommatesList = getRegistrantsAsRoommates(registrations, exclusionIds);
  if (roommateSearchText && roommateSearchText.length >= MIN_SEARCH_LENGTH) {
    return {
      guests: filterRoommates(roommatesList.guests, roommateSearchText),
      primaryInvitee: filterRoommates(roommatesList.primaryInvitee, roommateSearchText)
    };
  }
  return roommatesList;
});

/**
 * get all available roommates to display for selection
 * @type {function(*, *): *}
 */
const getAllAvailableRoommates = defaultMemoize((roommates, exclusionIds) => {
  const { availableRoommates: { all: availableRoommates } } = roommates;
  return availableRoommates
    .filter(roommate => !exclusionIds.includes(roommate.hotelReservationDetailId || roommate.contactId))
    .map(roommate => {
      const { firstName, lastName, ...roommateWithoutName } = roommate;
      return {
        ...getRoommateIdAndType(roommate),
        name: formatAttendeeName({ firstName, lastName }),
        ...roommateWithoutName
      };
    });
});

/**
 * get filtered roommates available to display for selection
 * @type {Function}
 */
const getFilteredAvailableRoommates = defaultMemoize((roommates, exclusionIds, roommateSearchText) => {
  if (roommateSearchText && roommateSearchText.length >= MIN_SEARCH_LENGTH) {
    const { availableRoommates: { keywordSearch } } = roommates;
    return (keywordSearch[roommateSearchText] || [])
      .filter(roommate => !exclusionIds.includes(roommate.hotelReservationDetailId || roommate.contactId))
      .map(roommate => {
        const { firstName, lastName, ...roommateWithoutName } = roommate;
        return {
          ...getRoommateIdAndType(roommate),
          name: formatAttendeeName({ firstName, lastName }),
          ...roommateWithoutName
        };
      });
  }
  return getAllAvailableRoommates(roommates, exclusionIds);
});

/**
 * Returns every possible roommate available for selection on UI while making a hotel request.
 * Can include guests or primary invitee from the ongoing registration, and all the other available registrants/invitees
 * in the event with matching criteria.
 * @param roommates
 * @param registrations
 * @param exclusionIds
 * @param roommateSearchText needed to filter out guests/invitees withing the registration.
 * other roommates are searched outside this.
 * @returns {{guests: [], availableRoommates: [], primaryInvitee: []}}
 */
export const getRoommateSearchList = defaultMemoize((
  roommates = { availableRoommates: { all: [], keywordSearch: {} } },
  registrations = { guests: [], primary: {} },
  exclusionIds = [],
  roommateSearchText
) => {
  return {
    ...getFilteredRegistrantsAsRoommates(registrations, exclusionIds, roommateSearchText),
    availableRoommates: getFilteredAvailableRoommates(roommates, exclusionIds, roommateSearchText)
  };
});

/**
 * Returns concatenated array of roommates
 * @param  {[]} allRoommates={guests:  [], primaryInvitee: [], availableRoommates: [] }
 */
export const getMergedRoommates =
  defaultMemoize((allRoommates = { guests: [], primaryInvitee: [], availableRoommates: [] }) => {
    return [
      ...allRoommates.guests,
      ...allRoommates.primaryInvitee,
      ...allRoommates.availableRoommates
    ];
  });

/**
 * returns suggestions list for roommate search textbox
 */
export const makeRoommateSuggestions = defaultMemoize((searchText, buildAll, props) => {
  if (props.isSiteEditorPreview) {
    return [];
  }
  const { roommates, registrations, roommateExclusions } = props;
  let roommatesToSuggest;
  if (buildAll) {
    roommatesToSuggest = getRoommateSearchList(roommates, registrations, roommateExclusions, '');
  } else {
    if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    roommatesToSuggest = getRoommateSearchList(roommates, registrations, roommateExclusions, searchText);
  }
  return getMergedRoommates(roommatesToSuggest);
});

/**
 * returns roommate name given a roommate id
 */
export const getRoommateNameById =
  defaultMemoize((roommateId, roommateIdType, availableRoommates, isSiteEditorPreview) => {
    if (isSiteEditorPreview) {
      return {
        firstName: '',
        lastName: ''
      };
    }
    let roommatesToSuggest = {};
    if (roommateIdType === RoommateIdType.EVENT_REGISTRATION) {
      roommatesToSuggest = availableRoommates.primaryInvitee;
      if (roommatesToSuggest.eventRegistrationId === roommateId) {
        return {
          firstName: roommatesToSuggest.attendee.personalInformation.firstName,
          lastName: roommatesToSuggest.attendee.personalInformation.lastName
        };
      }
    } else if (roommateIdType === RoommateIdType.GUEST_EVENT_REGISTRATION) {
      roommatesToSuggest = availableRoommates.guests.find(guest =>
        guest.eventRegistrationId === roommateId);
      if (roommatesToSuggest) {
        return {
          firstName: roommatesToSuggest.attendee.personalInformation.firstName,
          lastName: roommatesToSuggest.attendee.personalInformation.lastName
        };
      }
    } else if (roommateIdType === RoommateIdType.CONTACT) {
      roommatesToSuggest = availableRoommates.availableRoommates.find(roommate =>
        roommate.contactId === roommateId);
      if (roommatesToSuggest) {
        return {
          firstName: roommatesToSuggest.firstName,
          lastName: roommatesToSuggest.lastName
        };
      }
    } else if (roommateIdType === RoommateIdType.HOTEL_RESERVATION_DETAIL) {
      roommatesToSuggest = availableRoommates.availableRoommates.find(roommate =>
        roommate.hotelReservationDetailId === roommateId);
      if (roommatesToSuggest) {
        return {
          firstName: roommatesToSuggest.firstName,
          lastName: roommatesToSuggest.lastName
        };
      }
    }
    return {
      firstName: '',
      lastName: ''
    };
  });

/**
 * returns roommate id type given a roommate id
 */
export function getRoommateIdTypeByRoommateId(allRoommates, roommateId) {
  let roommatesToSuggest = allRoommates.find(roommate => roommate.roommateId === roommateId);
  if (roommatesToSuggest) {
    return roommatesToSuggest.roommateIdType;
  }
  return '';
}

/**
 * takes requested-from object and convert into format suitable for dropdown option, also adds a blank option
 * @param requestedFrom, the list of roommate object that have made request to this invitee,
 * hotelReservationDetailId should be present in this object
 * @param isSiteEditorPreview
 * @returns {*}
 */
export function makeRoommateAcceptFromOptions(requestedFrom = [], isSiteEditorPreview) {
  if (isSiteEditorPreview) {
    return [{
      name: 'John Doe',
      value: 'dummy'
    }];
  }

  let acceptFrom = requestedFrom.map(roommate => {
    return {
      name: formatAttendeeName({
        firstName: roommate.firstName,
        lastName: roommate.lastName
      }).trim(),
      value: roommate.hotelReservationDetailId
    };
  });
  const blankValue = {
    name: '',
    value: null
  };

  return [blankValue, ...acceptFrom];
}
/**
 * Returns display text for number of characters left
 * @param {number} char
 * @param {Function} translate
 * @returns {string}
 */
export function getTextAreaCharLimitText(char, translate) {
  return char ? translate('EventWidgets_AirRequest_Charlimit__resx', { char }) :
    translate('EventWidgets_AirRequest_ZeroCharLeft__resx');
}

/**
 * Converts event startDate and endDate from UTC to display time
 * @param {Object} state
 * @returns {Object}
 */
export const transformEventDates = createSelector(
  state => state.event,
  state => state.timezones,
  (event, timezones) => {
    const timezone = timezones[event.timezone];
    return {
      ...event,
      startDate: utcToDisplayDate(event.startDate, timezone),
      endDate: utcToDisplayDate(event.endDate, timezone),
      timezone
    };
  }
);

export const getRequestDateStatusOptions = (translate) => {
  return [
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Confirmed__resx'),
      value: REQUEST_DATE_STATUS.CONFIRMED },
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Pending__resx'),
      value: REQUEST_DATE_STATUS.PENDING },
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Denied__resx'),
      value: REQUEST_DATE_STATUS.DENIED }
  ];
};

/**
 * Returns translated request status values submitted by default.
 */
export const getRequestStatusKey = defaultMemoize((requestStatus) => {
  switch (requestStatus) {
    case REQUEST_STATUS.SUBMITTED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
    case REQUEST_STATUS.PROCESSING:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusProcessing__resx';
    case REQUEST_STATUS.APPROVED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusApproved__resx';
    case REQUEST_STATUS.RESERVED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusReserved__resx';
    case REQUEST_STATUS.DENIED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusDenied__resx';
    case REQUEST_STATUS.NOT_AVAILABLE:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusNotAvailable__resx';
    case REQUEST_STATUS.CANCELLED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusCancelled__resx';
    case REQUEST_STATUS.PENDING:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusPending__resx';
    case REQUEST_STATUS.TICKETED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusTicketed__resx';
    default:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
  }
});

/**
 * returns option object for request status listing
 */
const getRequestStatusOptionObject = defaultMemoize((translate, requestStatus) => {
  return { name: translate(getRequestStatusKey(requestStatus)),
    value: requestStatus };
});

/**
 * makes options list for request status dropdown control
 */
export const makeRequestStatusPlannerFieldOptions = defaultMemoize((translate, forAir) => {
  const options = [
    getRequestStatusOptionObject(translate, REQUEST_STATUS.SUBMITTED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.PROCESSING),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.APPROVED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.RESERVED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.DENIED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.NOT_AVAILABLE),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.CANCELLED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.PENDING)
  ];
  if (forAir) {
    options.push(getRequestStatusOptionObject(translate, REQUEST_STATUS.TICKETED));
  }
  return options;
});

/**
 * gets the currency symbol for the given currency id
 */
export const getCurrencySymbol = (currencyList = [], currencyId) => {
  const selectedCurrency = currencyList.find(currency => currency.id === currencyId);
  return (selectedCurrency ? selectedCurrency.nameOfSymbol : null);
};


/**
 * return merged roommate settings based on presence of roommateRequestSettings
 * @param configRoommateSettings
 * @param roommateRequestSettings
 * @returns {*}
 */
export const getMergedRoommateSettings = (configRoommateSettings, roommateRequestSettings) => {
  const defaultRoommateSettings = getRoommateRequestSettings();
  if (!roommateRequestSettings || !roommateRequestSettings.roommatePreferenceType) {
    return {
      ...defaultRoommateSettings,
      ...mapFromRoommateSetupSettings(configRoommateSettings)
    };
  }
  return null;
};

/**
 * Returns true if survery type belongs to travel questions
 * @param {string} surveyType
 * @returns {boolean}
 */
export function isTravelQuestion(surveyType) {
  return [
    SURVEY_TYPE.AIR_ACTUAL_QUESTIONS, SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.AIR_QUESTIONS, SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.HOTEL_QUESTIONS, SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.GROUP_FLIGHT_QUESTIONS, SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS
  ].includes(surveyType);
}

/**
 * Returns true if survery type belongs to travel alternate questions
 * @param {string} surveyType
 * @returns {boolean}
 */
export function isAlternateQuestion(surveyType) {
  return [
    SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS,
    SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS
  ].includes(surveyType);
}

/**
 * Returns associations for travel questions
 * @param {string} surveyType
 * @returns {Array}
 */
export function getTravelQuestionAssociations(surveyType) {
  switch (surveyType) {
    case SURVEY_TYPE.AIR_ACTUAL_QUESTIONS:
    case SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS:
      return ['airActual'];
    case SURVEY_TYPE.AIR_QUESTIONS:
    case SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS:
      return ['airRequest'];
    case SURVEY_TYPE.HOTEL_QUESTIONS:
    case SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS:
      return ['hotelRequest'];
    case SURVEY_TYPE.GROUP_FLIGHT_QUESTIONS:
    case SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS:
      return ['groupFlightRequest'];
    default:
      return [];
  }
}

/**
 * Air actual should be visible when:
 * 1. in site editor view
 * 2. for only planner if isPlannerDisplay option is set
 * and if display settings are present for air actual setup (use case when having different regPaths)
 * @type {boolean}
 */
export function shouldShowAirActualWidget(airActualSetup, isSiteEditorPreview, isPlannerRegistrationMode) {
  const isPlannerDisplayOnly = airActualSetup.isPlannerDisplayOnly;

  return airActualSetup.displaySettings && (isSiteEditorPreview || !isPlannerDisplayOnly || isPlannerRegistrationMode);
}

/**
 * Returns true if answers exist for provided metaData
 * @param {Object} questionsMetaData
 * @param {Array} travelAnswers
 * @returns {boolean}
 */
function hasTravelAnswers(questionsMetaData, travelAnswers) {
  const answersedQuestionIds = travelAnswers.map(a => a.questionId);
  return questionsMetaData.some(metaData => {
    return answersedQuestionIds.includes(metaData.question.id);
  });
}

/**
 * Returns whether to display travel questions on registration summary or not
 * @param {Object} questionsMetaData
 * @param {Array} travelAnswers
 * @param {Object} questionsConfig
 * @returns {boolean}
 */
export function displayTravelQuestions({ requestQuestionMetaData, alternateQuestionMetaData },
  travelAnswers, questionsConfig) {
  return (hasTravelAnswers([...requestQuestionMetaData, ...alternateQuestionMetaData], travelAnswers)
    || (questionsConfig.displayType === QUESTION_DISPLAY_TYPE.ALL && alternateQuestionMetaData.length > 0));
}

/**
 * Returns filtered question by survey type
 * @param {Array} questionsMetadata
 * @param {string} surveyType
 * @returns {Array}
 */
export function filterTravelQuestionsBySurveryType(questionsMetadata, surveyType) {
  return questionsMetadata.filter(q =>
    q.question.isTravelQuestion &&
    q.question.additionalInfo.surveyType === surveyType
  );
}

function getRoommateStatusFromRoommatePreferenceType(roommatePreferenceType, roommatePreferences,
  roomRequest, translate) {
  let roommateStatusText = '';
  switch (roommatePreferenceType) {
    case ROOMMATE_MATCHING_TYPE.NAME:
      if (roommatePreferences.roommateDescription) {
        roommateStatusText = roommatePreferences.roommateDescription;
        break;
      } else {
        return '';
      }
    case ROOMMATE_MATCHING_TYPE.ENLIST_SELF:
      roommateStatusText = `<em>(${translate('EventWidgets_HotelRequest_AddedToTheList__resx')})</em>`;
      break;
    case ROOMMATE_MATCHING_TYPE.SEARCH:
    case ROOMMATE_MATCHING_TYPE.ACCEPT:
      roommateStatusText = getRoommateStatusTextOnRoommateSelection(roomRequest, translate);
      break;
    default:
      if (roommatePreferences) {
        roommateStatusText = getRoommateStatusTextOnRoommateSelection(roomRequest, translate);
      } else {
        return '';
      }
  }
  return roommateStatusText;
}

export const getRoommateMatchingStatusText = defaultMemoize((roomRequest, translate, isSiteEditor) => {
  if (!roomRequest.isMultiOccupancyRoom || !roomRequest.preferences.isRoomSharingOpted) {
    return '';
  } else if (isSiteEditor) {
    return translate('EventWidgets_HotelRequest_LabelRoommate__resx');
  }
  const roommatePreferences = roomRequest.preferences && roomRequest.preferences.roommatePreferences;
  const roommatePreferenceType = roommatePreferences && roomRequest.preferences.roommatePreferences.type;

  const roommateStatusText = getRoommateStatusFromRoommatePreferenceType(roommatePreferenceType, roommatePreferences,
    roomRequest, translate);

  return roommateStatusText ? `${translate('EventWidgets_HotelRequest_LabelRoommate__resx')}&nbsp;${roommateStatusText}` : '';
});

function checkIfRoommateNameBlank(roommateName) {
  let isRoommateNameBlank = false;
  roommateName.split(',').forEach(elem => {
    if (elem.trim().length > 0) {
      isRoommateNameBlank = true;
    }
  });
  return isRoommateNameBlank;
}

function getRoommateStatusTextOnRoommateSelection(roomRequest, translate) {
  let roommateStatusText = '';
  const roommatePreferences = roomRequest.preferences.roommatePreferences;
  let roommateName = formatAttendeeName({
    firstName: roommatePreferences.roommateFirstName,
    lastName: roommatePreferences.roommateLastName
  });

  // add additional attendee names
  if (roommatePreferences.additionalRoommates && roommatePreferences.additionalRoommates.length) {
    roommatePreferences.additionalRoommates.map(r => {
      roommateName = concatAttendeeName(roommateName, formatAttendeeName({
        firstName: r.firstName,
        lastName: r.lastName
      }));
    });
  }

  if (roomRequest.isRoommateMatchingFailed) {
    roommateStatusText = `<em>(${translate('EventWidgets_HotelRequest_NoRoommateAssigned__resx')})</em>`;
  } else if (roomRequest.preferences.roommatePreferences.roommateMatchingStatus === RoommateStatus.DENIED) {
    roommateStatusText = `<em>(${translate('EventWidgets_HotelRequest_PreviousRoommateDenied__resx')})</em>`;
  } else if (roomRequest.isRoommateMatched) {
    roommateStatusText = checkIfRoommateNameBlank(roommateName) ? `${roommateName}&nbsp;<em>(${translate('EventWidgets_HotelRequest_ConfirmedRoommate__resx')})</em>` : '';
  } else {
    roommateStatusText = checkIfRoommateNameBlank(roommateName) ? `${roommateName}&nbsp;<em>(${translate('EventWidgets_HotelRequest_UnconfirmedRoommate__resx')})</em>` : '';
  }

  return roommateStatusText;
}
